// src/services/profileService.js

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import UserProfile from "../models/UserProfile";
import { db } from "../firebase";

// Helper function to get user profile from localStorage
export const getUserProfileFromLocalStorage = () => {
  const userProfile = localStorage.getItem("userProfile");
  return userProfile ? JSON.parse(userProfile) : null;
};

// Firestore profile functions
export const updateProfileInFirestore = async (uid, updatedProfile) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, updatedProfile);
};

// Fetch Firestore profile
export const fetchUserProfileFromFirestore = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  // console.log("Fetching Firestore profile for UID:", uid); // Debugging Log

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    // console.log("Firestore User Profile Data:", userSnap.data()); // Debugging Log
    return userSnap.data();
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null; // Return null instead of throwing an error
  }
};

// Delete Firestore profile
export const deleteUserProfileFromFirestore = async (uid) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);
  await deleteDoc(userRef);
};

export const createUserProfileInFirestore = async (uid, profileData) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  // Ensure the 'uid' field is part of the profileData
  if (!profileData.uid) {
    profileData.uid = uid;
  }

  const userRef = doc(db, "users", uid);
  await setDoc(userRef, profileData);
};

// Azure profile fetching function
export const fetchAzureUserProfile = async (email) => {
  const getUserProfileUrl =
    process.env.REACT_APP_GET_KBEAN_USER_PROFILE_BY_EMAIL_URL;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  console.log("Fetching Azure user profile for email:", email); // Log email

  try {
    const response = await fetch(getUserProfileUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ApiKey: apiKey,
      },
      body: JSON.stringify({ EmailAddress: email }),
    });

    console.log("Azure API response status:", response.status); // Log status code

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    console.log("Azure profile response data:", data); // Log the entire response

    return data;
  } catch (error) {
    console.error("Error fetching Azure profile:", error); // Log any errors
    throw error; // Re-throw the error to handle it in the calling function
  }
};

// Check username availability via 3rd party API
export const checkUserNameAvailability = async (userName) => {
  const checkUserNameAvailabilityURL =
    process.env.REACT_APP_CHECK_USERNAME_AVAILABILITY_URL;
  const response = await fetch(`${checkUserNameAvailabilityURL}${userName}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data.response.message === "UserName available";
};

// Update Azure user profile with new username
export const updateAzureUserProfileUsername = async (userName, email) => {
  const updateUserNameUrl = process.env.REACT_APP_UPDATE_KBEAN_USERNAME;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  const response = await fetch(updateUserNameUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ApiKey: apiKey,
    },
    body: JSON.stringify({ UserName: userName, EmailAddress: email }),
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return await response.json();
};

// Fetch challenges created by a user
export const fetchUserChallenges = async (uid, currentUser) => {
  if (!uid) throw new Error("User ID is required to fetch challenges");

  const challengesRef = collection(db, "challenges");

  // If fetching challenges for the current user, include drafts
  let q;
  if (uid === currentUser) {
    // Fetch both published and unpublished challenges for the current user
    q = query(challengesRef, where("creatorInfo.creatorUid", "==", uid));
  } else {
    // Fetch only published challenges for other users
    q = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", uid),
      where("published", "==", true)
    );
  }

  try {
    const querySnapshot = await getDocs(q);
    const challenges = [];
    querySnapshot.forEach((doc) => {
      challenges.push({ id: doc.id, ...doc.data() });
    });

    return challenges; // Return the fetched challenges
  } catch (error) {
    console.error("Error fetching challenges:", error);
    throw error;
  }
};

// Fetch submissions created by a user
export const fetchUserSubmissions = async (uid, currentUserUid) => {
  if (!uid) throw new Error("User ID is required to fetch submissions");

  const submissionsRef = collection(db, "submissions");
  const q = query(submissionsRef, where("profileData.uid", "==", uid));

  try {
    const querySnapshot = await getDocs(q);
    const submissions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Omit submissions with status "draft" if currentUserUid is not equal to uid
      if (data.status === "draft" && currentUserUid !== uid) {
        return; // Skip this submission
      }
      submissions.push({ id: doc.id, ...data });
    });

    return submissions;
  } catch (error) {
    console.error("Error fetching submissions:", error);
    throw error;
  }
};

// Function to update profile image in the relevant collections
export const updateProfileImageInAllCollections = async (
  userId,
  newProfileImageUrl
) => {
  try {
    // Step 1: Update profile image in "challenges" collection
    const challengesRef = collection(db, "challenges");
    const challengesQuery = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", userId)
    );
    const challengesSnapshot = await getDocs(challengesQuery);

    challengesSnapshot.forEach(async (challengeDoc) => {
      const challengeRef = doc(db, "challenges", challengeDoc.id);
      await updateDoc(challengeRef, {
        "creatorInfo.profileImage": newProfileImageUrl,
      });
    });

    console.log("Updated profile image in challenges collection.");

    // Step 2: Update profile image in "submissions" collection
    const submissionsRef = collection(db, "submissions");
    const submissionsQuery = query(
      submissionsRef,
      where("user.uid", "==", userId)
    );
    const submissionsSnapshot = await getDocs(submissionsQuery);

    submissionsSnapshot.forEach(async (submissionDoc) => {
      const submissionRef = doc(db, "submissions", submissionDoc.id);
      await updateDoc(submissionRef, {
        "user.profileImage": newProfileImageUrl,
      });
    });

    console.log("Updated profile image in submissions collection.");
  } catch (error) {
    console.error("Error updating profile image in all collections:", error);
  }
};

// Function to follow a user
export const followUser = async (currentUser, targetUser) => {
  const followerUserProfile = getUserProfileFromLocalStorage();

  console.log(followerUserProfile.userName);

  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Add the follower to the target user's followers collection
    await setDoc(followerRef, {
      uid: targetUser.uid,
      followerUid: followerUserProfile.uid,
      followerDisplayName: followerUserProfile.displayName,
      followerUsername: followerUserProfile.userName,
      followerProfileImage: followerUserProfile.profileImage,
      timestamp: new Date(),
    });

    // Add the current user to the target user's following collection
    await setDoc(followingRef, {
      uid: currentUser.uid,
      followingUid: targetUser.uid,
      followingDisplayName: targetUser.displayName,
      followingUsername: targetUser.userName,
      followingProfileImage: targetUser.profileImage,
      timestamp: new Date(),
    });

    console.log("Successfully followed the user");
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

// Function to unfollow a user
export const unfollowUser = async (currentUser, targetUser) => {
  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Delete follower and following documents
    await deleteDoc(followerRef);
    await deleteDoc(followingRef);

    console.log("Successfully unfollowed the user");
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

// Function to check if the current user is following the target user
export const isFollowingUser = async (currentUser, targetUserId) => {
  try {
    const followingDocId = `${currentUser.uid}_${targetUserId}`;
    const followingRef = doc(db, "following", followingDocId);
    const followingDoc = await getDoc(followingRef);

    return followingDoc.exists();
  } catch (error) {
    console.error("Error checking following status:", error);
    return false;
  }
};

// Function to fetch followers count for a specific user
export const fetchFollowersCount = async (userId) => {
  try {
    const followersRef = collection(db, "followers");
    const followersQuery = query(followersRef, where("uid", "==", userId));
    const followersSnapshot = await getDocs(followersQuery);
    return followersSnapshot.size;
  } catch (error) {
    console.error("Error fetching followers count:", error);
    throw error;
  }
};

// Function to fetch following count for a specific user
export const fetchFollowingCount = async (userId) => {
  try {
    const followingRef = collection(db, "following");
    const followingQuery = query(followingRef, where("uid", "==", userId));
    const followingSnapshot = await getDocs(followingQuery);
    return followingSnapshot.size;
  } catch (error) {
    console.error("Error fetching following count:", error);
    throw error;
  }
};

// Function to fetch artist profile with artworks from Kbean Azure backend for social video interactions
export const getArtistProfile = async (username) => {
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;
  const url = process.env.REACT_APP_GET_KBEAN_ARTIST_PROFILE_BY_USERNAME;

  const body = JSON.stringify({
    Sort: '{"Sale.SaleStart":-1}',
    Page: 0,
    Size: 2000,
    GroupBy: "",
    FilterMatureArtworks: true,
    FilterKbeanSelectOnly: false,
    Criteria: [
      {
        type: "username",
        value: username,
      },
    ],
  });

  const headers = {
    "Content-Type": "application/json",
    ApiKey: apiKey,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch artist profile:", error);
    throw error;
  }
};

// Helper function to fetch a user profile from Firestore by UID
export const fetchUserProfile = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() };
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null;
  }
};

// Fetch the current logged-in user's profile from Firestore
export const fetchCurrentUserProfile = async (currentUser) => {
  if (currentUser) {
    try {
      const userProfile = await fetchUserProfile(currentUser.uid);
      return userProfile || currentUser; // Return Firestore profile or auth profile
    } catch (error) {
      console.error("Error fetching Firestore profile:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return null; // Return null if no user is logged in
  }
};

// Function to fetch memberships for a user from Firestore
export const fetchUserMembershipsFromFirestore = async (uid) => {
  console.log(`Fetching memberships for UID: ${uid}`); // Debugging Log
  const membershipsRef = collection(db, "memberships");
  const q = query(membershipsRef, where("userId", "==", uid));

  const querySnapshot = await getDocs(q);
  const memberships = [];

  querySnapshot.forEach((doc) => {
    console.log(`Found membership: ${doc.id}`); // Debugging Log
    memberships.push({ id: doc.id, ...doc.data() });
  });

  return memberships;
};

// Fetch the current logged-in user's memberships from Firestore
export const fetchCurrentUserMemberships = async (currentUser) => {
  if (currentUser) {
    try {
      const userMemberships = await fetchUserMembershipsFromFirestore(
        currentUser.uid
      );
      return userMemberships;
    } catch (error) {
      console.error("Error fetching Firestore memberships:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return []; // Return an empty array if no user is logged in
  }
};

export const getOrCreateUserProfile = async (user) => {
  // Try fetching the profile from Firestore
  let firestoreProfile = await fetchUserProfileFromFirestore(user.uid);
  if (firestoreProfile) {
    return firestoreProfile;
  }

  // Otherwise, fetch from Azure and create a new Firestore profile
  const azureProfile = await fetchAzureUserProfile(user.email);
  const newUserProfile = new UserProfile({
    uid: user.uid,
    displayName: azureProfile.fullName || user.displayName || "New User",
    email: azureProfile.emailAddress || user.email,
    userName: azureProfile.userName || user.email,
    bio: azureProfile.biography || "",
    profileImage: azureProfile.profilePicture || user.photoURL || "",
  });

  await createUserProfileInFirestore(user.uid, newUserProfile.toPlainObject());
  return newUserProfile;
};
